import { useEffect, useMemo, useState } from 'react';
import { getStorageItem, setStorageItem } from '../utils/xummStorage';

type EvidenceDocType =
  | 'invoice'
  | 'purchase_confirmation'
  | 'assay_bar_list'
  | 'vault_receipt'
  | 'insurance_certificate'
  | 'audit_report'
  | 'legal_opinion'
  | 'other';

type EvidenceItem = {
  id: string;
  docType: EvidenceDocType;
  filename: string;
  sizeBytes: number;
  lastModifiedMs: number;
  sha256Hex: string;
  note?: string;
  addedAtIso: string;
};

type EvidenceBundle = {
  type: 'UNYKORN_EVIDENCE_BUNDLE';
  version: 1;
  generatedAtIso: string;
  items: EvidenceItem[];
};

type InstrumentManifest = {
  type: 'UNYKORN_INSTRUMENT_MANIFEST';
  version: 1;
  generatedAtIso: string;
  instrumentName: string;
  seriesId?: string;
  issuerEntity?: string;
  jurisdiction?: string;
  description?: string;
  collateral?: {
    description?: string;
  };
  riskNotes?: string;
  evidenceBundleSha256?: string;
};

const STORAGE_KEY = 'unykorn.evidenceVault.v1';

function formatErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function bytesToHex(bytes: ArrayBuffer): string {
  const b = new Uint8Array(bytes);
  return Array.from(b)
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase();
}

async function sha256HexFromBytes(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return bytesToHex(digest);
}

async function sha256HexFromString(text: string): Promise<string> {
  const encoded = new TextEncoder().encode(text);
  return sha256HexFromBytes(encoded.buffer);
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(obj).sort()) {
      out[k] = canonicalize(obj[k]);
    }
    return out;
  }
  return value;
}

function canonicalJsonString(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

async function safeLoadItems(): Promise<EvidenceItem[]> {
  try {
    const raw = await getStorageItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is EvidenceItem => {
      if (!x || typeof x !== 'object') return false;
      const r = x as Record<string, unknown>;
      return typeof r.id === 'string' && typeof r.filename === 'string' && typeof r.sha256Hex === 'string';
    });
  } catch {
    return [];
  }
}

async function safeSaveItems(items: EvidenceItem[]) {
  try {
    await setStorageItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
}

export default function EvidenceVault(props: {
  isMobile: boolean;
  onSetXrpMemo?: (memo: string) => void;
  onSetIouMemo?: (memo: string) => void;
  onSetPathMemo?: (memo: string) => void;
}) {
  const { isMobile, onSetIouMemo, onSetPathMemo, onSetXrpMemo } = props;

  const [items, setItems] = useState<EvidenceItem[]>([]);

  // Load items from storage on mount
  useEffect(() => {
    (async () => {
      const loaded = await safeLoadItems();
      setItems(loaded);
    })();
  }, []);

  // Save items whenever they change
  useEffect(() => {
    safeSaveItems(items);
  }, [items]);

  const [docType, setDocType] = useState<EvidenceDocType>('invoice');
  const [note, setNote] = useState('');
  const [hashing, setHashing] = useState(false);
  const [hashError, setHashError] = useState<string | null>(null);

  const [instrumentName, setInstrumentName] = useState('');
  const [instrumentSeries, setInstrumentSeries] = useState('');
  const [instrumentIssuer, setInstrumentIssuer] = useState('');
  const [instrumentJurisdiction, setInstrumentJurisdiction] = useState('');
  const [instrumentDesc, setInstrumentDesc] = useState('');

  useEffect(() => {
    safeSaveItems(items);
  }, [items]);

  const bundle: EvidenceBundle = useMemo(() => {
    return {
      type: 'UNYKORN_EVIDENCE_BUNDLE',
      version: 1,
      generatedAtIso: new Date().toISOString(),
      items
    };
  }, [items]);

  const [bundleHash, setBundleHash] = useState<string>('');
  const bundleCanonical = useMemo(() => canonicalJsonString(bundle), [bundle]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const h = await sha256HexFromString(bundleCanonical);
        if (!cancelled) setBundleHash(h);
      } catch {
        if (!cancelled) setBundleHash('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bundleCanonical]);

  const memoText = useMemo(() => {
    if (!bundleHash) return '';
    return `EVIDENCE_SHA256:${bundleHash}`;
  }, [bundleHash]);

  const canExportEvidence = items.length > 0;

  const instrumentManifest: InstrumentManifest = useMemo(() => {
    return {
      type: 'UNYKORN_INSTRUMENT_MANIFEST',
      version: 1,
      generatedAtIso: new Date().toISOString(),
      instrumentName: instrumentName.trim() || 'Untitled Instrument',
      seriesId: instrumentSeries.trim() || undefined,
      issuerEntity: instrumentIssuer.trim() || undefined,
      jurisdiction: instrumentJurisdiction.trim() || undefined,
      description: instrumentDesc.trim() || undefined,
      evidenceBundleSha256: bundleHash || undefined,
      collateral: {
        description: items.length ? `Evidence bundle with ${items.length} file hash(es)` : undefined
      },
      riskNotes: 'This manifest is metadata for audit trails. It is not legal/financial advice and does not create investor rights.'
    };
  }, [bundleHash, instrumentDesc, instrumentIssuer, instrumentJurisdiction, instrumentName, instrumentSeries, items.length]);

  const [instrumentHash, setInstrumentHash] = useState<string>('');
  const instrumentCanonical = useMemo(() => canonicalJsonString(instrumentManifest), [instrumentManifest]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const h = await sha256HexFromString(instrumentCanonical);
        if (!cancelled) setInstrumentHash(h);
      } catch {
        if (!cancelled) setInstrumentHash('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instrumentCanonical]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Copied.');
    } catch {
      alert('Copy failed.');
    }
  };

  const addFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setHashError(null);
    setHashing(true);

    try {
      const next: EvidenceItem[] = [];
      for (const f of Array.from(files)) {
        const buf = await f.arrayBuffer();
        const h = await sha256HexFromBytes(buf);

        next.push({
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          docType,
          filename: f.name,
          sizeBytes: f.size,
          lastModifiedMs: f.lastModified,
          sha256Hex: h,
          note: note.trim() ? note.trim() : undefined,
          addedAtIso: new Date().toISOString()
        });
      }

      setItems((prev) => [...next, ...prev]);
    } catch (e) {
      setHashError(formatErrorMessage(e));
    } finally {
      setHashing(false);
    }
  };

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  const clearAll = () => {
    const ok = window.confirm('Clear all evidence items from this browser?');
    if (!ok) return;
    setItems([]);
  };

  const cardStyle: React.CSSProperties = {
    padding: isMobile ? '22px' : '28px',
    background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.10) 0%, rgba(15, 23, 42, 0.25) 100%)',
    border: '1px solid rgba(251, 191, 36, 0.22)',
    borderRadius: '16px',
    marginBottom: '32px'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px',
    background: 'rgba(0, 0, 0, 0.4)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '14px'
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '12px', color: '#fde68a', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 800 }}>
            Evidence Vault
          </div>
          <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 600, color: '#ffffff', marginTop: '8px' }}>
            Local-only document hashing + memo anchors
          </div>
          <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '10px', lineHeight: 1.6, maxWidth: '980px' }}>
            Files never leave your device. We compute SHA-256 hashes in-browser, export an evidence bundle JSON, and generate a short memo string you can attach to XRPL transactions.
          </div>
        </div>

        <div style={{ padding: '12px 14px', background: 'rgba(0, 0, 0, 0.35)', border: '1px solid rgba(255, 255, 255, 0.10)', borderRadius: '12px' }}>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '6px' }}>
            Bundle SHA-256
          </div>
          <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#fde68a', wordBreak: 'break-all', maxWidth: '420px' }}>
            {bundleHash || '…'}
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => copy(bundleHash)}
              disabled={!bundleHash}
              style={{
                padding: '10px 12px',
                fontSize: '12px',
                fontWeight: '800',
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '10px',
                color: '#ffffff',
                cursor: bundleHash ? 'pointer' : 'not-allowed',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                opacity: bundleHash ? 1 : 0.6
              }}
            >
              Copy
            </button>
            <button
              onClick={() => copy(memoText)}
              disabled={!memoText}
              style={{
                padding: '10px 12px',
                fontSize: '12px',
                fontWeight: '800',
                background: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                color: '#000000',
                cursor: memoText ? 'pointer' : 'not-allowed',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                opacity: memoText ? 1 : 0.6
              }}
            >
              Copy Memo
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '0.6fr 1.4fr', gap: '16px', marginTop: '18px' }}>
        <div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Document type</div>
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value as EvidenceDocType)}
            style={inputStyle}
          >
            <option value="invoice">Invoice</option>
            <option value="purchase_confirmation">Purchase Confirmation</option>
            <option value="assay_bar_list">Assay / Bar List</option>
            <option value="vault_receipt">Vault Receipt</option>
            <option value="insurance_certificate">Insurance Certificate</option>
            <option value="audit_report">Audit Report</option>
            <option value="legal_opinion">Legal Opinion</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Note (optional)</div>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g., Vault cert # / Policy # / Inspection date"
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px', alignItems: 'center' }}>
        <label
          style={{
            padding: '14px 18px',
            fontSize: '13px',
            fontWeight: 900,
            background: hashing ? 'rgba(251, 191, 36, 0.25)' : '#FBBF24',
            border: 'none',
            borderRadius: '10px',
            color: '#000000',
            cursor: hashing ? 'not-allowed' : 'pointer',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          {hashing ? 'Hashing…' : 'Add Files'}
          <input
            type="file"
            multiple
            onChange={(e) => {
              void addFiles(e.target.files);
              // reset input so re-selecting same file works
              e.currentTarget.value = '';
            }}
            style={{ display: 'none' }}
            disabled={hashing}
          />
        </label>

        <button
          disabled={!canExportEvidence}
          onClick={() => {
            const payload: EvidenceBundle & { bundleSha256: string } = {
              ...bundle,
              bundleSha256: bundleHash
            };
            downloadJson(`uny-evidence-bundle-${bundleHash.slice(0, 12)}.json`, payload);
          }}
          style={{
            padding: '14px 18px',
            fontSize: '13px',
            fontWeight: 800,
            background: canExportEvidence ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: '10px',
            color: canExportEvidence ? '#ffffff' : '#9ca3af',
            cursor: canExportEvidence ? 'pointer' : 'not-allowed',
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}
        >
          Export Evidence JSON
        </button>

        <button
          disabled={!memoText}
          onClick={() => onSetXrpMemo?.(memoText)}
          style={{
            padding: '14px 18px',
            fontSize: '13px',
            fontWeight: 800,
            background: 'rgba(16, 185, 129, 0.12)',
            border: '1px solid rgba(16, 185, 129, 0.25)',
            borderRadius: '10px',
            color: memoText ? '#a7f3d0' : '#9ca3af',
            cursor: memoText && onSetXrpMemo ? 'pointer' : 'not-allowed',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            opacity: memoText && onSetXrpMemo ? 1 : 0.6
          }}
          title="Inject into the XRP Payment memo field"
        >
          Use Memo (XRP)
        </button>

        <button
          disabled={!memoText}
          onClick={() => onSetIouMemo?.(memoText)}
          style={{
            padding: '14px 18px',
            fontSize: '13px',
            fontWeight: 800,
            background: 'rgba(59, 130, 246, 0.12)',
            border: '1px solid rgba(59, 130, 246, 0.25)',
            borderRadius: '10px',
            color: memoText ? '#bfdbfe' : '#9ca3af',
            cursor: memoText && onSetIouMemo ? 'pointer' : 'not-allowed',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            opacity: memoText && onSetIouMemo ? 1 : 0.6
          }}
          title="Inject into the IOU Payment memo field"
        >
          Use Memo (IOU)
        </button>

        <button
          disabled={!memoText}
          onClick={() => onSetPathMemo?.(memoText)}
          style={{
            padding: '14px 18px',
            fontSize: '13px',
            fontWeight: 800,
            background: 'rgba(168, 85, 247, 0.12)',
            border: '1px solid rgba(168, 85, 247, 0.25)',
            borderRadius: '10px',
            color: memoText ? '#ddd6fe' : '#9ca3af',
            cursor: memoText && onSetPathMemo ? 'pointer' : 'not-allowed',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            opacity: memoText && onSetPathMemo ? 1 : 0.6
          }}
          title="Inject into the Path Payment memo field"
        >
          Use Memo (Path)
        </button>

        <button
          onClick={clearAll}
          disabled={items.length === 0}
          style={{
            padding: '14px 18px',
            fontSize: '13px',
            fontWeight: 800,
            background: 'rgba(239, 68, 68, 0.10)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            borderRadius: '10px',
            color: items.length ? '#fecaca' : '#9ca3af',
            cursor: items.length ? 'pointer' : 'not-allowed',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            opacity: items.length ? 1 : 0.6
          }}
        >
          Clear
        </button>
      </div>

      {hashError && (
        <div
          style={{
            marginTop: '14px',
            padding: '14px',
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.25)',
            borderRadius: '12px',
            color: '#fecaca',
            fontSize: '13px',
            lineHeight: 1.6
          }}
        >
          Hashing error: {hashError}
        </div>
      )}

      {items.length > 0 && (
        <div style={{ marginTop: '18px' }}>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Evidence items ({items.length})
          </div>

          <div style={{ display: 'grid', gap: '10px' }}>
            {items.slice(0, 10).map((x) => (
              <div
                key={x.id}
                style={{
                  padding: '12px 14px',
                  background: 'rgba(0, 0, 0, 0.35)',
                  border: '1px solid rgba(255, 255, 255, 0.10)',
                  borderRadius: '12px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '13px', color: '#ffffff', fontWeight: 700 }}>{x.filename}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      {x.docType} • {(x.sizeBytes / 1024).toFixed(1)} KB
                      {x.note ? ` • ${x.note}` : ''}
                    </div>
                    <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#fde68a', marginTop: '8px', wordBreak: 'break-all' }}>
                      {x.sha256Hex}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => copy(x.sha256Hex)}
                      style={{
                        padding: '10px 12px',
                        fontSize: '12px',
                        fontWeight: '800',
                        background: 'rgba(255, 255, 255, 0.08)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '10px',
                        color: '#ffffff',
                        cursor: 'pointer',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Copy Hash
                    </button>
                    <button
                      onClick={() => removeItem(x.id)}
                      style={{
                        padding: '10px 12px',
                        fontSize: '12px',
                        fontWeight: '800',
                        background: 'rgba(239, 68, 68, 0.10)',
                        border: '1px solid rgba(239, 68, 68, 0.25)',
                        borderRadius: '10px',
                        color: '#fecaca',
                        cursor: 'pointer',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {items.length > 10 && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: '#9ca3af' }}>
              Showing first 10 items. Export the evidence bundle JSON to capture the full list.
            </div>
          )}
        </div>
      )}

      {/* Instrument Manifest */}
      <div style={{ marginTop: '22px', paddingTop: '18px', borderTop: '1px solid rgba(255, 255, 255, 0.10)' }}>
        <div style={{ fontSize: '12px', color: '#fde68a', textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 800 }}>
          Instrument Manifest
        </div>
        <div style={{ fontSize: '13px', color: '#9ca3af', marginTop: '10px', lineHeight: 1.6 }}>
          Generate a metadata manifest that references the Evidence Bundle hash. This is for audit trails and internal operations.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px', marginTop: '14px' }}>
          <div>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Instrument name</div>
            <input value={instrumentName} onChange={(e) => setInstrumentName(e.target.value)} placeholder="Gold-backed note / Reserve note" style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Series ID (optional)</div>
            <input value={instrumentSeries} onChange={(e) => setInstrumentSeries(e.target.value)} placeholder="Series A / 2026-01" style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Issuer entity (optional)</div>
            <input value={instrumentIssuer} onChange={(e) => setInstrumentIssuer(e.target.value)} placeholder="SPV legal name" style={inputStyle} />
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Jurisdiction (optional)</div>
            <input value={instrumentJurisdiction} onChange={(e) => setInstrumentJurisdiction(e.target.value)} placeholder="e.g., BVI / Cayman / Delaware" style={inputStyle} />
          </div>
        </div>

        <div style={{ marginTop: '14px' }}>
          <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>Description (optional)</div>
          <input value={instrumentDesc} onChange={(e) => setInstrumentDesc(e.target.value)} placeholder="Purpose + control + reporting cadence" style={inputStyle} />
        </div>

        <div style={{ marginTop: '14px', padding: '12px 14px', background: 'rgba(0, 0, 0, 0.35)', border: '1px solid rgba(255, 255, 255, 0.10)', borderRadius: '12px' }}>
          <div style={{ fontSize: '12px', color: '#9ca3af' }}>Manifest SHA-256</div>
          <div style={{ fontSize: '12px', fontFamily: 'monospace', color: '#fde68a', wordBreak: 'break-all', marginTop: '6px' }}>
            {instrumentHash || '…'}
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => copy(instrumentHash)}
              disabled={!instrumentHash}
              style={{
                padding: '10px 12px',
                fontSize: '12px',
                fontWeight: '800',
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: '10px',
                color: '#ffffff',
                cursor: instrumentHash ? 'pointer' : 'not-allowed',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                opacity: instrumentHash ? 1 : 0.6
              }}
            >
              Copy
            </button>
            <button
              onClick={() => {
                downloadJson(`uny-instrument-manifest-${instrumentHash.slice(0, 12)}.json`, {
                  ...instrumentManifest,
                  manifestSha256: instrumentHash
                });
              }}
              disabled={!instrumentHash}
              style={{
                padding: '10px 12px',
                fontSize: '12px',
                fontWeight: '900',
                background: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                color: '#000000',
                cursor: instrumentHash ? 'pointer' : 'not-allowed',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                opacity: instrumentHash ? 1 : 0.6
              }}
            >
              Export Manifest
            </button>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '18px', fontSize: '12px', color: '#9ca3af', lineHeight: 1.6 }}>
        Tip: for tonight’s release, the safest on-chain pattern is to attach the memo to a real transaction you’re already doing (Payment / TrustSet), and keep the full bundle JSON in your internal archive.
      </div>
    </div>
  );
}
